import { useEffect, useState } from 'react';
import { useFetchWebpageVisualSeoAnalysisProcessQuery } from '../../service';


/**
 * Polls the active visual SEO analysis process for a given { wGuid, url } every 10s while
 * a process is active. Because the backend's `GET /active` endpoint auto-creates a process
 * when none exists and no finished analysis is available, the first load naturally yields an
 * active process (never an empty page). Transitions off when the process completes.
 */
export default function useWebpageVisualSeoAnalysisProgress(wGuid: string, url: string) {
    const [hasActiveProcess, setHasActiveProcess] = useState<boolean>(false);
    const [processGuid, setProcessGuid] = useState<string | null>(null);

    const { data: visualSeoAnalysisProcess, isLoading, error, isError } = useFetchWebpageVisualSeoAnalysisProcessQuery(
        { wGuid, url, processGuid },
        {
            skip: !wGuid || !url,
            pollingInterval: hasActiveProcess ? 10_000 : 0,
            refetchOnMountOrArgChange: true,
        }
    );
    const analysisError = visualSeoAnalysisProcess?.output?.error || null;

    useEffect(() => {
        // We didn't have an active process but now we do
        if (visualSeoAnalysisProcess && hasActiveProcess === false) {
            setHasActiveProcess(true);
            setProcessGuid(visualSeoAnalysisProcess.guid);
        }

        // Active process completed
        if (visualSeoAnalysisProcess && visualSeoAnalysisProcess.status === 'completed' && hasActiveProcess === true) {
            setHasActiveProcess(false);
            setProcessGuid(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visualSeoAnalysisProcess]);

    return {
        analysisError,
        isLoading,
        error,
        isError,
        hasActiveProcess,
        processGuid,
    };
}
